// App.js
import { useEffect, useState, useRef, useCallback, createContext } from "react"
import Header from "./components/Header"
import WebsiteDetails from "./components/WebsiteDetails" // Make sure WebsiteDetails is imported
import ExportControls from "./components/ExportControls"
import DataTable from "./components/DataTable"
import HomePage from "./components/Homepage"
import { getProductsData, LIMIT_PER_PAGE, getTotalProductsCount } from "./components/ProductsData" // Import getTotalProductsCount
import { getCollectionsData } from "./components/CollectionsData"
import Login from "./components/Login"
import SignUp from "./components/SignUp"
import AdminDashboard from "./components/AdminDashboard"
import UserDashboard from "./components/UserDashboard"
import CreateUser from "./components/CreateUser"
import WhatsAppButton from "./components/WhatsAppButton"
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSpinner, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons"
import { checkAuth, logoutUser } from "./utils/auth"
import Features from "./components/Features"
import Pricing from "./components/Pricing"
import FAQ from "./components/FAQ"
import Blog from "./components/Blog"
import BlogPost from "./components/BlogPost"
import Contact from "./components/Contact"
import TermsOfService from "./components/TermsOfService"
import HowItWorks from "./components/HowItWorks"
import Footer from "./components/Footer"
import Subscription from "./components/subscription"
import PaymentPage from "./components/PaymentsPage"
import AboutUs from "./components/AboutUs"
import PrivacyPolicy from "./components/PrivacyPolicy"

export const PlanContext = createContext(null)

const COLLECTION_PRODUCTS_LIMIT_PER_PAGE = 25

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [userRole, setUserRole] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authChecked, setAuthChecked] = useState(false)

  useEffect(() => {
    const verifyAuth = async () => {
      setIsLoading(true)
      try {
        console.log("Verifying authentication status...")
        const { isAuthenticated, user } = await checkAuth()
        console.log("Auth check result:", isAuthenticated, user)
        setIsAuthenticated(isAuthenticated)
        setUserRole(user?.role || null)
      } catch (error) {
        console.error("Auth verification error:", error)
        setIsAuthenticated(false)
        setUserRole(null)
      } finally {
        setIsLoading(false)
        setAuthChecked(true)
      }
    }
    verifyAuth()
  }, [])

  const login = (userData) => {
    setIsAuthenticated(true)
    setUserRole(userData.role)
  }

  const logout = async () => {
    const success = await logoutUser()
    if (success) {
      setIsAuthenticated(false)
      setUserRole(null)
    }
  }
  return { isAuthenticated, userRole, login, logout, isLoading, authChecked }
}

const ProtectedRoute = ({ children, isAuthenticated, isLoading, requiredRole, userRole }) => {
  if (isLoading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} className="spinner" />
        <p className="loading-text">Verifying authentication...</p>
      </div>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (requiredRole && userRole !== requiredRole) {
    return <Navigate to="/" replace />
  }
  return children
}

const App = () => {
  const [websiteUrl, setWebsiteUrl] = useState("") // Stores the successfully validated URL
  const [allProducts, setAllProducts] = useState([])
  const [exportType, setExportType] = useState("Products")
  const [isLoading, setIsLoading] = useState(false) // General loading for products/collections
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalProductsCount, setTotalProductsCount] = useState(0)

  // New state for total items loading
  const [isTotalItemsLoading, setIsTotalItemsLoading] = useState(false);

  const [exportPlatform, setExportPlatform] = useState("Shopify")
  const [viewingCollectionHandle, setViewingCollectionHandle] = useState(null)
  const [collectionProducts, setCollectionProducts] = useState([])
  const [collectionProductsLoading, setCollectionProductsLoading] = useState(false)
  const [collectionProductsError, setCollectionProductsError] = useState(null)
  const [collectionProductsPage, setCollectionProductsPage] = useState(1)
  const [totalCollectionProductsCount, setTotalCollectionProductsCount] = useState(0)
  const [totalCollectionPages, setTotalCollectionPages] = useState(1)

  const [plan, setPlan] = useState(null)
  const { isAuthenticated, userRole, login, logout, isLoading: authLoading, authChecked } = useAuth()
  const location = useLocation()

  // State to hold comprehensive website details (favicon, name, title, isShopify)
  const [websiteDetails, setWebsiteDetails] = useState({ url: '', favicon: '', name: '', title: '', isShopify: false });

  // NEW STATE FOR WEBSITE FETCH ERROR specifically related to URL validation/initial fetch
  const [websiteFetchError, setWebsiteFetchError] = useState(null);

  const fetchData = useCallback(
    async (urlToProcess, pageNumber = 1) => { // Accept URL directly from WebsiteDetails
      if (!isAuthenticated || !urlToProcess) {
          setIsLoading(false);
          return;
      }

      setIsLoading(true);
      setAllProducts([]);
      setCurrentPage(pageNumber);
      setViewingCollectionHandle(null);
      setCollectionProducts([]);
      setCollectionProductsPage(1);
      setTotalCollectionProductsCount(0);
      setTotalCollectionPages(1);
      setWebsiteFetchError(null); // Clear previous fetch errors

      let isShopifyStore = false; // Flag to determine if it's a Shopify store

      try {
        // Step 1: Extract general website info (favicon, name, title)
        let favicon = `https://www.google.com/s2/favicons?domain=${urlToProcess}&sz=64`;
        let name = '';
        let title = '';

        // Extract name from URL
        const domainRegex = /^(?:https?:\/\/)?(?:www\.)?([^\/\.]+)\.([^\/]+)/i;
        const matches = urlToProcess.match(domainRegex);
        if (matches && matches.length > 1) {
          name = matches[1];
        } else {
          name = urlToProcess.replace(/^https?:\/\//, '')
                              .replace(/^www\./, '')
                              .replace(/\/.*$/, '')
                              .split('.')[0];
        }

        try {
          // Attempt to fetch title (Note: This will likely be blocked by CORS unless proxied on backend)
          // For now, mirroring WebsiteDetails.js logic. In production, this needs a backend proxy.
          const response = await fetch(`https://cors-anywhere.herokuapp.com/${urlToProcess}`);
          if (response.ok) {
            const html = await response.text();
            const titleMatch = html.match(/<title>(.*?)<\/title>/i);
            if (titleMatch && titleMatch[1]) {
              title = titleMatch[1];
            }
          }
        } catch (titleError) {
          console.warn('Could not fetch page title (likely CORS issue, try a backend proxy):', titleError);
        }

        // Step 2: Fetch products/collections data and determine if it's a Shopify store
        let fetchedData;
        if (exportType === "Products") {
          fetchedData = await getProductsData(urlToProcess, pageNumber);
          isShopifyStore = fetchedData && fetchedData.length > 0;
        } else if (exportType === "Collections") {
          fetchedData = await getCollectionsData(urlToProcess);
          isShopifyStore = fetchedData && fetchedData.length > 0;
        }

        if (!isShopifyStore) {
          throw new Error("The entered URL is not a valid Shopify store or no products/collections could be fetched.");
        }

        // Update main App.js states after successful fetch and Shopify validation
        setWebsiteUrl(urlToProcess); // Set the main websiteUrl state
        setWebsiteDetails({ // Populate websiteDetails object
          url: urlToProcess,
          favicon: favicon,
          name: name,
          title: title,
          isShopify: true, // It's a Shopify store if we reached here successfully
        });

        setAllProducts(fetchedData);

        if (exportType === "Products") {
          if (fetchedData.length < LIMIT_PER_PAGE) {
            setTotalPages(pageNumber);
            // setTotalProductsCount((pageNumber - 1) * LIMIT_PER_PAGE + fetchedData.length); // This is just for current page count
          } else {
            setTotalPages(pageNumber + 1); // Assume there might be more if we got full page
            // setTotalProductsCount(pageNumber * LIMIT_PER_PAGE + (fetchedData.length === LIMIT_PER_PAGE ? LIMIT_PER_PAGE : fetchedData.length)); // More accurate total count
          }
        } else if (exportType === "Collections") {
          setTotalPages(1);
          setTotalProductsCount(fetchedData.length); // For collections, total count is straightforward
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setWebsiteFetchError(error.message || "An unexpected error occurred while fetching data.");
        // Clear all related states on error
        setWebsiteUrl("");
        setWebsiteDetails({ url: '', favicon: '', name: '', title: '', isShopify: false });
        setAllProducts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [exportType, isAuthenticated], // Dependencies for useCallback
  );

  useEffect(() => {
    const fetchPlanData = async () => {
      if (!isAuthenticated) return
      try {
        const response = await fetch("/api/dashboard", { credentials: "include" })
        if (!response.ok) throw new Error("Failed to fetch plan data")
        const data = await response.json()
        setPlan(data.plan)
      } catch (error) {
        console.error("App.js: Failed to fetch plan type:", error)
      }
    }
    fetchPlanData()
  }, [isAuthenticated])

  useEffect(() => {
    if (!authChecked) return
    const params = new URLSearchParams(window.location.search)
    const url = params.get("url")

    if (url && isAuthenticated && location.pathname === "/home") {
      const decodedUrl = decodeURIComponent(url)
      // Call fetchData to process the URL from query params
      fetchData(decodedUrl, 1);
      const newUrl = new URL(window.location.href)
      newUrl.searchParams.delete("url")
      window.history.replaceState({}, document.title, newUrl.toString())
    }
  }, [isAuthenticated, authChecked, location.pathname, fetchData])


  useEffect(() => {
    if (authChecked && isAuthenticated && location.pathname === "/home" && websiteUrl) {
      // Pass the current websiteUrl state to fetchData
      fetchData(websiteUrl, currentPage);
    } else if (authChecked && isAuthenticated && location.pathname === "/home" && !websiteUrl) {
      setIsLoading(false);
    } else if (authChecked && !isAuthenticated && location.pathname === "/home") {
      setIsLoading(false);
    }
  }, [websiteUrl, exportType, fetchData, isAuthenticated, viewingCollectionHandle, authChecked, location.pathname, currentPage]);

  // New useEffect to calculate total products count asynchronously
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component
    const calculateTotal = async () => {
      if (websiteUrl && exportType === "Products") {
        setIsTotalItemsLoading(true);
        try {
          const count = await getTotalProductsCount(websiteUrl);
          if (isMounted) {
            setTotalProductsCount(count);
          }
        } catch (error) {
          console.error("Failed to calculate total product count:", error);
          if (isMounted) {
            setTotalProductsCount(0); // Reset or handle error count
          }
        } finally {
          if (isMounted) {
            setIsTotalItemsLoading(false);
          }
        }
      } else if (exportType === "Collections") {
         // If it's collections, total count is already set by fetchData
         setIsTotalItemsLoading(false);
      }
    };

    calculateTotal();

    return () => {
      isMounted = false; // Cleanup: set flag to false when component unmounts
    };
  }, [websiteUrl, exportType]); // Recalculate when URL or exportType changes


  const handlePlatformChange = useCallback((platform) => {
    setExportPlatform(platform)
  }, [])

  const goToPage = useCallback(
    (pageNumber) => {
      if (pageNumber > 0 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber)
        fetchData(websiteUrl, pageNumber) // Pass websiteUrl
      }
    },
    [fetchData, totalPages, websiteUrl], // Add websiteUrl to dependencies
  )

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1)
    }
  }, [currentPage, totalPages, goToPage])

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      goToPage(currentPage - 1)
    }
  }, [currentPage, goToPage])

  const fetchCollectionProducts = useCallback(
    async (collectionHandle, pageNumber = 1, limit = COLLECTION_PRODUCTS_LIMIT_PER_PAGE) => {
      if (!isAuthenticated || !websiteUrl) { // uses websiteUrl state
          setCollectionProductsLoading(false);
          return [];
      }
      try {
        const apiUrl = `${websiteUrl}/collections/${collectionHandle}/products.json?page=${pageNumber}&limit=${limit}`
        const response = await fetch(apiUrl)
        if (!response.ok) {
          throw new Error(`Failed to fetch collection products on page ${pageNumber}: ${response.status}`)
        }
        const data = await response.json()
        const formattedProducts = data.products.map((product) => ({
          id: product.id,
          title: product.title,
          vendor: product.vendor,
          image: product.images && product.images.length > 0 ? product.images[0].src : null,
          link: `${websiteUrl}/products/${product.handle}`,
          handle: product.handle,
        }))
        return formattedProducts
      } catch (error) {
        console.error("Error fetching collection products:", error)
        return []
      }
    },
    [websiteUrl, isAuthenticated],
  )

  const handleViewCollectionProducts = useCallback(
    (collectionHandle) => {
        if (!isAuthenticated) {
            console.log("Attempted to view collection products without authentication. Redirecting to login or preventing action.");
            return;
        }

      setViewingCollectionHandle(collectionHandle)
      setExportType("CollectionProducts")
      setCollectionProducts([])
      setCollectionProductsPage(1)
      setTotalCollectionProductsCount(0)
      setTotalCollectionPages(1)

      setCollectionProductsLoading(true)
      setCollectionProductsError(null)

      fetchCollectionProducts(collectionHandle, 1, COLLECTION_PRODUCTS_LIMIT_PER_PAGE)
        .then((products) => {
          setCollectionProducts(products)
          if (products.length < COLLECTION_PRODUCTS_LIMIT_PER_PAGE) {
            setTotalCollectionPages(1)
            setTotalCollectionProductsCount(products.length)
          } else {
            setTotalCollectionPages(2)
            setTotalCollectionProductsCount(COLLECTION_PRODUCTS_LIMIT_PER_PAGE)
          }
        })
        .catch((error) => {
          console.error("Error fetching initial collection products:", error)
          setCollectionProductsError(error.message)
        })
        .finally(() => setCollectionProductsLoading(false))
    },
    [fetchCollectionProducts, isAuthenticated],
  )

  useEffect(() => {
    if (isAuthenticated && viewingCollectionHandle && exportType === "CollectionProducts") {
      setCollectionProductsLoading(true)
      setCollectionProductsError(null)
      fetchCollectionProducts(viewingCollectionHandle, collectionProductsPage, COLLECTION_PRODUCTS_LIMIT_PER_PAGE)
        .then((products) => {
          setCollectionProducts(products)
          if (products.length < COLLECTION_PRODUCTS_LIMIT_PER_PAGE) {
            setTotalCollectionPages(collectionProductsPage)
            setTotalCollectionProductsCount((collectionProductsPage - 1) * COLLECTION_PRODUCTS_LIMIT_PER_PAGE + products.length)
          } else {
            setTotalCollectionPages(collectionProductsPage + 1)
            setTotalCollectionProductsCount(collectionProductsPage * COLLECTION_PRODUCTS_LIMIT_PER_PAGE)
          }
        })
        .catch((error) => {
          console.error("Error fetching collection products:", error)
          setCollectionProductsError(error.message)
        })
        .finally(() => setCollectionProductsLoading(false))
    } else if (authChecked && !isAuthenticated && location.pathname === "/home" && viewingCollectionHandle) {
        setCollectionProductsLoading(false);
    }
  }, [viewingCollectionHandle, exportType, collectionProductsPage, fetchCollectionProducts, isAuthenticated, authChecked, location.pathname])


  const handleBackToCollections = useCallback(() => {
    if (!isAuthenticated) {
        console.log("Attempted to go back to collections without authentication. User should be redirected or button disabled.");
        return;
    }
    setViewingCollectionHandle(null)
    setExportType("Collections")
    setCollectionProducts([])
    setCollectionProductsPage(1)
    setTotalCollectionProductsCount(0)
    setTotalCollectionPages(1)
    fetchData(websiteUrl) // Pass websiteUrl
  }, [fetchData, isAuthenticated, websiteUrl]) // Add websiteUrl to dependencies

  const goToCollectionPage = useCallback(
    (pageNumber) => {
      if (pageNumber > 0 && pageNumber <= totalCollectionPages) {
        setCollectionProductsPage(pageNumber)
      }
    },
    [totalCollectionPages],
  )

  const nextCollectionPage = useCallback(() => {
    if (collectionProductsPage < totalCollectionPages) {
      goToCollectionPage(collectionProductsPage + 1)
    }
  }, [collectionProductsPage, totalCollectionPages, goToCollectionPage])

  const prevCollectionPage = useCallback(() => {
    if (collectionProductsPage > 1) {
      goToCollectionPage(collectionProductsPage - 1)
    }
  }, [collectionProductsPage, goToCollectionPage])

  const productsToDisplay = exportType === "CollectionProducts" ? collectionProducts : allProducts
  const loadingData = isLoading || collectionProductsLoading
  const errorData = collectionProductsError

  if (!authChecked || authLoading) {
    return (
      <div
        className="loading-container"
        style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <FontAwesomeIcon icon={faSpinner} className="spinner" />
        <p className="loading-text">Checking authentication status...</p>
      </div>
    )
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    const maxPageNumbersToShow = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2))
    let endPage = Math.min(totalPages, startPage + maxPageNumbersToShow - 1)

    if (endPage - startPage + 1 < maxPageNumbersToShow) {
      startPage = Math.max(1, endPage - maxPageNumbersToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            currentPage === i ? "bg-teal-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          disabled={isLoading || exportType !== "Products" || !isAuthenticated}
        >
          {i}
        </button>,
      )
    }
    return pageNumbers
  }

  const renderCollectionPageNumbers = () => {
    const pageNumbers = []
    const maxPageNumbersToShow = 5
    let startPage = Math.max(1, collectionProductsPage - Math.floor(maxPageNumbersToShow / 2))
    let endPage = Math.min(totalCollectionPages, startPage + maxPageNumbersToShow - 1)

    if (endPage - startPage + 1 < maxPageNumbersToShow) {
      startPage = Math.max(1, endPage - maxPageNumbersToShow + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={`col-${i}`}
          onClick={() => goToCollectionPage(i)}
          className={`px-3 py-1 mx-1 rounded ${
            collectionProductsPage === i ? "bg-teal-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
          disabled={collectionProductsLoading || !isAuthenticated}
        >
          {i}
        </button>,
      )
    }
    return pageNumbers
  }

  return (
    <PlanContext.Provider value={plan?.type || null}>
      <div className="app-container">
        <style jsx="true">{`
          /* Global styles */
          body {
            margin: 0;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans",
              "Helvetica Neue", sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            background-color: #f9fafb;
            color: #111827;
          }

          *,
          *::before,
          *::after {
            box-sizing: border-box;
          }

          /* App container */
          .app-container {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
          }

          /* Main content area */
          .main-content {
            width: 100%;
            margin: 0 auto;
            padding: 1.5rem;
            flex: 1;
          }

          /* Loading overlay */
          .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgb(255, 255, 255);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 1000;
            backdrop-filter: blur(3px);
          }

          .export-button:disabled {
            background-color: #d1d5db;
            color: #6b7280;
            cursor: not-allowed;
          }

          /* Loading spinner */
          .spinner {
            color: #007BFF;
            font-size: 3rem;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }

          .loading-text {
            margin-top: 1rem;
            font-size: 1rem;
            color: #4b5563;
            font-weight: 500;
          }

          /* Error message */
          .error-message {
            background-color: #fee2e2;
            border: 1px solid #fca5a5;
            color: #b91c1c;
            padding: 1rem;
            border-radius: 0.375rem;
            margin: 1.5rem 0;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .error-icon {
            color: #ef4444;
            font-size: 1.25rem;
          }

          /* Pagination specific styles */
          .pagination-controls {
            display: flex;
            justify-content: center;
            align-items: center;
            padding: 1rem 0;
            margin-top: 1rem;
            gap: 0.5rem;
          }

          .pagination-controls button {
            padding: 0.5rem 1rem;
            border: 1px solid #d1d5db;
            border-radius: 0.25rem;
            background-color: #f3f4f6;
            color: #4b5563;
            cursor: pointer;
            transition: background-color 0.2s, border-color 0.2s;
          }

          .pagination-controls button:hover:not(:disabled) {
            background-color: #e5e7eb;
            border-color: #9ca3af;
          }

          .pagination-controls button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .pagination-controls .active-page {
            background-color: #007BFF;
            color: white;
            border-color: #007BFF;
          }

          .pagination-info {
            margin: 0 1rem;
            color: #4b5563;
            font-size: 0.9rem;
          }

          /* Responsive adjustments */
          @media (max-width: 768px) {
            .main-content {
              padding: 1rem;
            }
            .pagination-controls {
              flex-wrap: wrap;
              justify-content: center;
            }
          }
        `}</style>

        <Header isAuthenticated={isAuthenticated} onLogout={logout} />

        <main className="main-content" style={{ pointerEvents: loadingData ? "none" : "auto" }}>
          <Routes>
            <Route path="/" element={<HomePage />} />

            <Route
              path="/home"
              element={
                authLoading || !authChecked ? (
                    <div className="loading-container">
                        <FontAwesomeIcon icon={faSpinner} className="spinner" />
                        <p className="loading-text">Checking authentication status...</p>
                    </div>
                ) : (
                    isAuthenticated ? (
                        <>
                            {/* Pass websiteUrl, websiteDetails properties, and onFetchData to WebsiteDetails */}
                            <WebsiteDetails
                                websiteUrl={websiteUrl} // This is the validated URL
                                onFetchData={(url) => fetchData(url, 1)} // Pass URL from WebsiteDetails to fetchData
                                faviconUrl={websiteDetails.favicon}
                                websiteName={websiteDetails.name}
                                websiteTitle={websiteDetails.title}
                                websiteFetchError={websiteFetchError} // Pass the error state
                            />

                            {/* Only render ExportControls, DataTable, and Pagination if websiteUrl is present and no fetch error */}
                            {websiteUrl && !websiteFetchError && (
                                <>
                                    <ExportControls
                                        totalItems={exportType === "Products" ? totalProductsCount : productsToDisplay.length}
                                        exportType={exportType === "CollectionProducts" ? "Products" : exportType}
                                        onExportTypeChange={setExportType}
                                        onPlatformChange={handlePlatformChange}
                                        onSearchChange={(query) => console.log(`Search Query: ${query}`)}
                                        onClear={() => console.log("Clear clicked")}
                                        websiteUrl={websiteUrl}
                                        viewingCollectionHandle={viewingCollectionHandle}
                                        onBackToCollections={handleBackToCollections}
                                        isAuthenticated={isAuthenticated}
                                        isTotalItemsLoading={isTotalItemsLoading} // Pass the new loading state
                                    />

                                    {loadingData && (
                                        <div className="loading-overlay">
                                            <FontAwesomeIcon icon={faSpinner} className="spinner" />
                                            <p className="loading-text">
                                                {exportType === "Products"
                                                    ? "Loading products..."
                                                    : exportType === "Collections"
                                                        ? "Loading collections..."
                                                        : "Loading collection products..."}
                                            </p>
                                        </div>
                                    )}

                                    {errorData ? (
                                        <div className="error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
                                            <span>Error: {errorData}</span>
                                        </div>
                                    ) : (
                                        <DataTable
                                            data={productsToDisplay}
                                            exportType={exportType}
                                            websiteUrl={websiteUrl}
                                            onViewCollectionProducts={handleViewCollectionProducts}
                                            isViewingCollectionProducts={exportType === "CollectionProducts"}
                                            onBackToCollections={handleBackToCollections}
                                            exportPlatform={exportPlatform}
                                            currentPage={exportType === "CollectionProducts" ? collectionProductsPage : currentPage}
                                            productsPerPage={exportType === "CollectionProducts" ? COLLECTION_PRODUCTS_LIMIT_PER_PAGE : LIMIT_PER_PAGE}
                                            plan={plan}
                                        />
                                    )}

                                    {exportType === "Products" && allProducts.length > 0 && (
                                        <div className="pagination-controls">
                                            <button onClick={prevPage} disabled={currentPage === 1 || isLoading || !isAuthenticated}>
                                                Previous
                                            </button>
                                            {renderPageNumbers()}
                                            <button onClick={nextPage} disabled={currentPage >= totalPages || isLoading || !isAuthenticated}>
                                                Next
                                            </button>
                                        </div>
                                    )}

                                    {exportType === "CollectionProducts" && collectionProducts.length > 0 && (
                                        <div className="pagination-controls">
                                            <button onClick={prevCollectionPage} disabled={collectionProductsPage === 1 || collectionProductsLoading || !isAuthenticated}>
                                                Previous
                                            </button>
                                            {renderCollectionPageNumbers()}
                                            <button
                                                onClick={nextCollectionPage}
                                                disabled={collectionProductsPage >= totalCollectionPages || collectionProductsLoading || !isAuthenticated}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <Navigate to="/login" replace />
                    )
                )
              }
            />

            <Route
              path="/login"
              element={
                <Login
                  onLogin={(userData) => {
                    login(userData)
                  }}
                />
              }
            />
            <Route path="/signup" element={<SignUp />} />

            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            <Route
              path="/admin"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  isLoading={authLoading}
                  requiredRole="admin"
                  userRole={userRole}
                >
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/create-user"
              element={
                <ProtectedRoute
                  isAuthenticated={isAuthenticated}
                  isLoading={authLoading}
                  requiredRole="admin"
                  userRole={userRole}
                >
                  <CreateUser />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={authLoading} userRole={userRole}>
                  <UserDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={authLoading} userRole={userRole}>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={authLoading} userRole={userRole}>
                  <PaymentPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
      </div>
    </PlanContext.Provider>
  )
}

export default App;